import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Grid, GridItem, Heading, HStack, IconButton, Input, Link, ListItem, Stack, Text, Tooltip, UnorderedList } from "~/foundation/Chakra";
import { useForm } from "react-hook-form";
import bgImage from "~/foundation/Assets/svg/graphics/alia-graphics.svg";
import { Icon } from "../Icon";
import { Loader } from "../Loading";
import ChatMarkdown from "./ChatMarkdown";
import {
	type AliaChatFeedback,
	type AliaChatQuestion,
	type AliaSettingsModel
} from "~/foundation/SitecoreContent/generated-types";
import { useTranslation } from "~/foundation/Dictionary";
import type { SearchDictionary } from "~/feature/Search/dictionary"; //eslint-disable-line
import type { ChatMessage, MessageStatus } from "./ChatBotDrawer";
import { aliaChatService } from "~/foundation/SitecoreContent/services/aliaChatService.ts";
import xss from "xss";
import { richTextXssSettings } from '~/foundation/Settings/xssSettings';
import { getMessageStyle } from './getMessageStyle';
import { getChatHistoryCookie, updateChatHistoryCookie } from './cookieHandler';

type FormValues = {
	question: string;
};

async function makeQuery(question: AliaChatQuestion): Promise<ChatMessage> {
	const requestBody: AliaChatQuestion = {
		question: question.question,
		chatlogId: question.chatlogId,
		contextUrl: window.location.href
	};

	// We make our own client side timestamp on outgoing messages.
	const timestamp = new Date().toLocaleTimeString();
	const response = await aliaChatService.chatQuestion(requestBody);

	let out: ChatMessage;
	if (response.data) {
		out = { ...response.data, status: "in" };
	} else {
		out = { response: `${response.error} [${response.statusCode}]`, status: "error", chatlogId: "", timestamp, sources: [] };
	}
	return out;
}

export type ChatBotProps = {
	isRtl: boolean;
	initialQuestion: string;
	initialPrompt: string;
	largeContentPadding?: boolean;
	formatMessageQuestions?: boolean;
	settings?: AliaSettingsModel;
	articleId?: string;
	onHistoryChange?: (chatlogId: string) => void;
}

export function ChatBot({
	isRtl = false,
	initialQuestion,
	initialPrompt,
	largeContentPadding = true,
	formatMessageQuestions = false,
	settings,
	articleId,
	onHistoryChange
}: ChatBotProps) {
	if (!settings) {
		return <></>;
	}

	const { register, handleSubmit, setFocus, resetField, formState } = useForm<FormValues>();
	const [history, setHistory] = useState<ChatMessage[]>([]);
	const [chatlogId, setChatlogId] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [feedBackSet, setFeedbackSet] = useState(false);
	const inputRef = useRef<HTMLDivElement>(null);
	const [t] = useTranslation<SearchDictionary>();

	const handleQuestion = useCallback(async (question: AliaChatQuestion, displayText?: string) => {
		// If we have a display text, show that instead of the actual question
		const visibleText = displayText || question.question;
		history.push({ response: visibleText, status: "out", chatlogId, timestamp: new Date().toLocaleTimeString(), sources: [] });
		setHistory([...history]); // Create new array to trigger update
		resetField("question");
		setFocus("question");
		setIsLoading(true);
		makeQuery(question).then((message) => {
			setChatlogId(message.chatlogId);
			message.timestamp = new Date().toLocaleTimeString();
			setHistory(prev => [...prev, message]);
			setFeedbackSet(false);
			setIsLoading(false);

			// Update chat history cookie if we have an articleId and chatlogId
			if (articleId && message.chatlogId) {
				updateChatHistoryCookie(articleId, message.chatlogId);
				onHistoryChange?.(message.chatlogId);
			}
		}).finally(() => {
			setTimeout(() => {
				inputRef.current && inputRef.current.scrollIntoView({ behavior: "smooth" })
			}, 10);
		});
	}, [chatlogId, articleId]);

	const handleFeedback = useCallback(async (feedback: AliaChatFeedback) => {
		await aliaChatService.feedbackMessage(feedback);
		setFeedbackSet(true);
	}, [chatlogId]);

	const onSubmit = handleSubmit(data => {
		if (!data?.question) return;

		const question: AliaChatQuestion = { question: data.question, chatlogId };
		handleQuestion(question).then(() => {
			inputRef.current && inputRef.current.scrollIntoView({ behavior: "smooth" })
		});
	});

	useEffect(() => {
		const loadChatHistory = async () => {
			if (articleId && history.length === 0) {
				const chatHistory = getChatHistoryCookie();
				const chatlogIds = chatHistory[articleId];

				if (chatlogIds?.length) {
					setIsLoading(true);
					try {
						for (const id of chatlogIds) {
							const response = await aliaChatService.getMessages(id);
							if (response.data?.messages) {
								const messages = response.data.messages
									.filter(msg => msg && typeof msg.content === 'string')
									.map(msg => ({
										response: msg.content,
										chatlogId: id,
										timestamp: msg.createdAt.toString(),
										sources: [],
										status: msg.type === "user" ? "out" : "in" as MessageStatus
									}));
								if (messages.length > 0) {
									setHistory(messages);
									setChatlogId(id);
								}
							}
						}
					} catch (error) {
						console.error('Failed to load chat history:', error);
					}
					setTimeout(() => {
						// Scroll to bottom. no smoothing, doesn't work with drawer anim.
						inputRef.current && inputRef.current.scrollIntoView();
					}, 10);
					setFocus("question");
					setIsLoading(false);
				} else {
					initializeChat();
				}
			} else if (history.length === 0) {
				initializeChat();
			}
		};

		loadChatHistory();
		setFocus("question");
	}, []);

	const initializeChat = () => {
		const text = initialPrompt || initialQuestion;
		if (text) {
			if (initialPrompt && initialQuestion) {
				handleQuestion({ question: initialPrompt, chatlogId }, initialQuestion);
			} else {
				handleQuestion({ question: text, chatlogId });
			}
		}
	}

	return (
		<Stack
			gap={6}
			bg="primary.darkBlue"
			color="primary.white"
			px={largeContentPadding ? ["var(--chakra-sizes-pageMarginSM)", null, null, "16"] : ["1.5rem", null, null, "2rem"]}
			pt={largeContentPadding ? ["6", null, null, "12"] : ["1.5rem", null, null, "2rem"]}
			pb={largeContentPadding ? ["6", null, null, "8"] : ["1.5rem", null, null, "2rem"]}
			h="full"
			roundedStart={{ base: 0, md: "lg" }}
			overflowY="auto"
			position="relative"
		>
			{history.length === 0 ? (
				<Box>
					<Stack
						justifyContent="center"
						ps={{ base: 0, md: "53%" }}
						bgImage={{ base: "none", md: bgImage }}
						h={{ base: "auto", md: "500px" }}
						bgRepeat="no-repeat"
						bgPos={isRtl ? "left center" : "right center"}
					>
						<Heading as="h1" fontSize="4xl" lineHeight={1}>{settings.introTitle}</Heading>
						<Text fontSize="lg">{settings.introDescription}</Text>
					</Stack>
					<Grid templateColumns="repeat(2, 1fr)" gap={{ base: 4, md: 12 }} my={12} fontSize={{ base: "xs", md: "sm" }}>
						{settings.introCards && settings.introCards.map((card, index) => (
							<GridItem colSpan={{ base: 2, sm: 1 }} key={`alia-intro-card-${index}`}>
								<Text fontWeight="bold">{card.title}</Text>
								<Text>{card.description}</Text>
							</GridItem>
						))}
					</Grid>
				</Box>
			) : (
				<Stack
					px={{ base: 6, md: 0 }}
					pt={{ base: 4, md: 0 }}
					pb={{ base: 4, md: 0 }}
					zIndex={1}
					w={{ base: "90%", md: "75%" }}
					gap={1}
					position={{ base: "fixed", md: "relative" }}
					top={0}
					insetStart={0}
				>
					<Text fontSize="xs" fontWeight={400} display="flex" alignItems="center"><Icon w={8} variant="Alia" /> {settings.introTitle}</Text>
					{settings?.terms && (<Box fontSize="2xs" sx={{
						a: { color: "primary.aramcoLinkBlue" }
					}} dangerouslySetInnerHTML={{ __html: xss(settings.terms, richTextXssSettings) }} />)}
				</Stack>
			)}

			{history.length &&
				<Stack
					gap={6}
					pb={3}
					pt={largeContentPadding ? { base: 24, md: 0 } : { base: 12, md: 0 }}
					position="relative"
				>
					{history.map((message, i) => (
						<HStack key={i} justifyContent={message.status === 'out' ? "flex-end" : "flex-start"}>
							<Flex
								px={6}
								{...getMessageStyle(message.status)}
								borderRadius="xl"
								fontSize="sm"
								fontWeight={300}
								maxW={i === 0 ? "60%" : "full"}
								flexDir="column"
								gap={[4, null, 6, null]}
							>
								<ChatMarkdown formatMessageQuestions={i === 1 && formatMessageQuestions} handleQuestion={handleQuestion} chatLogId={message.chatlogId}>
									{i === 0 ? initialQuestion : message.response}
								</ChatMarkdown>
								{message.sources.length > 0 && (
									<Box my={5}>
										<Text fontWeight="bold" mb={3}>{t("search.askAliaSources")}:</Text>
										<UnorderedList my={0} mx={0}>
											{message.sources.map(({ url, title }) => (
												<ListItem key={url} my={1} listStyleType="none" display="flex" gap={3}>
													<Icon color="primary.aramcoLinkBlue" w={4} variant="ArrowRight" />
													<Link color="primary.aramcoLinkBlue" href={url}>{title}</Link>
												</ListItem>
											))}
										</UnorderedList>
									</Box>
								)}
							</Flex>
						</HStack>
					))}
					{isLoading && (
						<HStack justifyContent="flex-start">
							<Box px={10} py={6} {...getMessageStyle("in")} borderRadius="xl">
								<Loader />
							</Box>
						</HStack>
					)}
				</Stack>
			}

			{chatlogId && !feedBackSet && (
				<HStack justifyContent="flex-end">
					<HStack gap={6}>
						<Tooltip label={t("search.askAliaFeedbackGood")} aria-label={t("search.askAliaFeedbackGood")} bgColor="lightgray" hasArrow px={3} py={2} borderRadius="sm">
							<IconButton icon={<Icon w={6} variant="ThumbUp" />} onClick={() => {
								handleFeedback({ feedback: "GOOD", chatlogId })
							}} aria-label="This is good" />
						</Tooltip>
						<Tooltip label={t("search.askAliaFeedbackBad")} aria-label={t("search.askAliaFeedbackBad")} bgColor="lightgray" hasArrow px={3} py={2} borderRadius="sm">
							<IconButton icon={<Icon w={6} variant="ThumbDown" />} onClick={() => {
								handleFeedback({ feedback: "BAD", chatlogId })
							}} aria-label="This is bad"
							/>
						</Tooltip>
					</HStack>
				</HStack>
			)}

			<Box mt="auto">
				<form onSubmit={onSubmit}>
					<Box
						borderRadius="full"
						bg="white"
						display="flex"
						ref={inputRef}
					>
						<Input
							{...register("question", { maxLength: { value: 250, message: t("search.askAliaMaxLengthError") } })}
							type="search"
							variant="unstyled"
							placeholder={settings.inputPlaceholderText}
							fontSize="md"
							fontWeight={300}
							color="primary.darkBlue"
							py={3}
							px={5}
							maxLength={251}
							disabled={isLoading}
						/>
						<Button onClick={onSubmit} me={5} aria-label={t("search.searchLabel")} disabled={isLoading}>
							<Icon variant="Alia" w={8} color="darkblue" />
						</Button>
					</Box>
					{formState?.errors.question && <Text size="2xs" color="red.300">{formState?.errors.question.message}</Text>}
				</form>
			</Box>
		</Stack>
	);
}
